import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import {
  IPeriodLockCustomerCell,
  IPeriodLockUserCell,
  PeriodLockDefinitionRecord,
} from './interfaces';
import {
  PERIOD_LOCK_DEFINITION_TABLE_KEY,
  PERIOD_LOCK_CUSTOMER_ID_KEY,
  PERIOD_LOCK_MODIFIED_AT_KEY,
  PERIOD_LOCK_MODIFIED_BY_KEY,
  PERIOD_LOCK_ACTIONS_KEY,
  PERIOD_LOCK_DEFINITION_CUSTOMER_ID_KEY,
  PERIOD_LOCK_DEFINITION_LOCK_TO,
  PERIOD_LOCK_DEFINITION_ENABLED,
  PERIOD_LOCK_DEFINITION_CREATED_BY,
  PERIOD_LOCK_DEFINITION_CREATED_AT,
  PERIOD_LOCK_DEFINITION_LOCK_AFTER,
} from './constants';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import CustomerCell from '../CmdTable/TableCells/CustomerCell';
import DateTimeCell from '../CmdTable/TableCells/DateTimeCell';
import ThSimpleSelect from '../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../CmdTable/TableCells/TextCell';
import ThOnlySort from '../CmdTable/TableHeadCell/ThOnlySort';
import IconCell from '../CmdTable/TableCells/IconCell';
import Actions from './RowAction/Actions';
import ChipsCellWithModal from './SpecificCell/ChipsCellWithModal';
import ThText from '../CmdTable/TableHeadCell/ThText';
import { customersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../CmdTable/CmdTableState';

interface IUsePeriodLock {
  columns: ColumnDef<PeriodLockDefinitionRecord>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function usePeriodLogDefinition(
  handleForm = (data: PeriodLockDefinitionRecord) => {
    console.info('table action');
  }
): IUsePeriodLock {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  // const tableFiltersKey = useRecoilValue(CmdTableState.tableFilters);
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(PERIOD_LOCK_DEFINITION_TABLE_KEY)
  );
  const customer = useRecoilValue(customersAtom);
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(PERIOD_LOCK_DEFINITION_TABLE_KEY)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);

  // const handleIndeterminateChange = (table) => {
  //   if (table.getIsSomeRowsSelected()) {
  //     // If currently indeterminate, unselect all.
  //     table.toggleAllRowsSelected(false);
  //   } else {
  //     // Otherwise, follow the default behavior.
  //     table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
  //   }
  // };

  // const updateTable = React.useCallback(() => {
  //   queryClient.invalidateQueries({
  //     queryKey: [PERIOD_LOCK_DEFINITION_TABLE_KEY, filterForRequest, sortFilter],
  //   });
  // }, [queryClient, filterForRequest, sortFilter]);

  // const handleRemove = React.useCallback(
  //   async (data: TPeriod) => {
  //     const ids = { [data.customer.id]: [data.id] };
  //     const response = await ApiService.put(`/v1/${TABLE_PERIOD_LOCK}/remove`, { ids }, true);
  //     if (response.data && response.data.status === 'ok') {
  //       updateTable();
  //     }
  //   },
  //   [updateTable]
  // );

  // const handleAction = React.useCallback(
  //   (data: PeriodLockDefinitionRecord) => {
  //     handleForm(data);
  //     // navigate(`${TABLE_PERIOD_LOCK}?customerId=${data.customer.id}`);
  //   },
  //   [handleForm]
  // );

  const handleCells = (data: PeriodLockDefinitionRecord) => {
    if (data.lockAfterPeriods.length > 0) {
      return <ChipsCellWithModal data={data} align="left" />;
    }
    return <TextCell text={data.lockAfter || ''} />;
  };

  const columns = React.useMemo<ColumnDef<PeriodLockDefinitionRecord>[]>(
    () => [
      // {
      //   id: 'select',
      //   header: ({ table }) => (
      //     <IndeterminateCheckbox
      //       {...{
      //         checked: table.getIsAllRowsSelected(),
      //         indeterminate: table.getIsSomeRowsSelected(),
      //         // onChange: table.getToggleAllRowsSelectedHandler(),
      //         onChange: () => handleIndeterminateChange(table),
      //       }}
      //     />
      //   ),
      //   cell: ({ row }) => (
      //     <div className="px-1">
      //       <IndeterminateCheckbox
      //         {...{
      //           checked: row.getIsSelected(),
      //           disabled: !row.getCanSelect(),
      //           indeterminate: row.getIsSomeSelected(),
      //           onChange: row.getToggleSelectedHandler(),
      //         }}
      //       />
      //     </div>
      //   ),
      //   size: 30,
      // },

      {
        accessorKey: PERIOD_LOCK_DEFINITION_CUSTOMER_ID_KEY,
        header: (table) => (
          <ThSimpleSelect
            tableName={PERIOD_LOCK_DEFINITION_TABLE_KEY}
            id={PERIOD_LOCK_CUSTOMER_ID_KEY}
            title={f({ id: `table_list.columns.customerId` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_CUSTOMER_ID_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.customer ? (
            <CustomerCell customer={info.getValue<IPeriodLockCustomerCell>()} />
          ) : (
            <TextCell text="" />
          ),
        // size: 260,
        minSize: 150,
      },
      {
        accessorKey: PERIOD_LOCK_DEFINITION_LOCK_TO,
        header: (table) => (
          <ThOnlySort
            id={PERIOD_LOCK_DEFINITION_LOCK_TO}
            tableName={PERIOD_LOCK_DEFINITION_TABLE_KEY}
            title={f({ id: `table_list.columns.periodLocks.${PERIOD_LOCK_DEFINITION_LOCK_TO}` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_DATE_FROM_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.lockTo ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: PERIOD_LOCK_DEFINITION_ENABLED,
        header: (table) => (
          <ThSimpleSelect
            id={PERIOD_LOCK_DEFINITION_ENABLED}
            tableName={PERIOD_LOCK_DEFINITION_TABLE_KEY}
            title={f({ id: `table_list.columns.periodLocks.${PERIOD_LOCK_DEFINITION_ENABLED}` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_DATE_TO_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.enabled ? (
            <IconCell isIcon={info.getValue<boolean>()} icon="cmd-icon-active text-success" />
          ) : (
            <TextCell text="" />
          ),
        size: 120,
      },
      {
        accessorKey: PERIOD_LOCK_DEFINITION_LOCK_AFTER,
        header: (table) => (
          <ThText
            // id={PERIOD_LOCK_DEFINITION_LOCK_AFTER}
            // tableName={PERIOD_LOCK_DEFINITION_TABLE_KEY}
            title={f({ id: `table_list.columns.periodLocks.${PERIOD_LOCK_DEFINITION_LOCK_AFTER}` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_DEFINITION_ID}.tooltip` })}
            // isSortable={true}
          />
        ),
        cell: (info) => info.row.original && handleCells(info.row.original),
        minSize: 260,
      },
      {
        accessorKey: PERIOD_LOCK_DEFINITION_CREATED_BY,
        header: (table) => (
          <ThMultiSelect
            tableName={PERIOD_LOCK_DEFINITION_TABLE_KEY}
            id={PERIOD_LOCK_DEFINITION_CREATED_BY}
            title={f({ id: `table_list.columns.${PERIOD_LOCK_DEFINITION_CREATED_BY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_PERIOD_LOCK_DEFINITION_CREATED_BY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <TextCell text={info.getValue<IPeriodLockUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        size: 160,
      },
      {
        accessorKey: PERIOD_LOCK_DEFINITION_CREATED_AT,
        header: (table) => (
          <ThDateTime
            id={PERIOD_LOCK_DEFINITION_CREATED_AT}
            tableName={PERIOD_LOCK_DEFINITION_TABLE_KEY}
            title={f({ id: `table_list.columns.${PERIOD_LOCK_DEFINITION_CREATED_AT}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_PERIOD_LOCK_DEFINITION_CREATED_AT}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: PERIOD_LOCK_MODIFIED_BY_KEY,
        header: (table) => (
          <ThMultiSelect
            tableName={PERIOD_LOCK_DEFINITION_TABLE_KEY}
            id={PERIOD_LOCK_MODIFIED_BY_KEY}
            title={f({ id: `table_list.columns.${PERIOD_LOCK_MODIFIED_BY_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_MODIFIED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<IPeriodLockUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: PERIOD_LOCK_MODIFIED_AT_KEY,
        header: (table) => (
          <ThDateTime
            id={PERIOD_LOCK_MODIFIED_AT_KEY}
            tableName={PERIOD_LOCK_DEFINITION_TABLE_KEY}
            title={f({ id: `table_list.columns.${PERIOD_LOCK_MODIFIED_AT_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_PERIOD_LOCK_MODIFIED_AT_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      {
        accessorKey: PERIOD_LOCK_ACTIONS_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail">
            {f({ id: 'table.actionsBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <Actions<PeriodLockDefinitionRecord> data={info.row.original} />
          ) : null,
        size: 60, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f]
  );

  const columnOrder = React.useMemo(() => {
    const order = columnsTable.map((item) => item.key);
    // add the detail and delete columns to the end
    order.push(PERIOD_LOCK_ACTIONS_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc, column) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
